import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
    flexDirection: 'column',
  },
  header: {
    marginBottom: 15,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  companyInfo: {
    width: '35%',
  },
  customerInfo: {
    width: '60%',
  },
  companyName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  companyDetails: {
    fontSize: 8,
    marginBottom: 2,
  },
  orderDetails: {
    fontSize: 8,
    marginBottom: 2,
  },
  title: {
    fontSize: 18,
    marginBottom: 10,
    textAlign: 'center',
  },
  section: {
    margin: 5,
    padding: 5,
  },
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#bfbfbf',
    marginTop: 10,
  },
  tableRow: {
    flexDirection: 'row',
    minHeight: 20,
  },
  tableRowWithBreak: {
    flexDirection: 'row',
    minHeight: 20,
    pageBreakInside: 'avoid',
  },
  tableCol: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#bfbfbf',
    padding: 4,
    justifyContent: 'center',
  },
  productCol: {
    width: '24%',
  },
  imageCol: {
    width: '6%',
  },
  colorCol: {
    width: '10%',
  },
  sizeCol: {
    width: '4.75%',
    textAlign: 'center',
  },
  priceCol: {
    width: '8%',
  },
  subtotalCol: {
    width: '8%',
  },
  totalCol: {
    width: '6%',
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
  },
  total: {
    marginTop: 20,
    textAlign: 'right',
    fontWeight: 'bold',
  },
  signature: {
    marginTop: 30,
    borderTopWidth: 1,
    borderTopColor: '#000000',
    paddingTop: 10,
    width: '30%',
    alignSelf: 'flex-end',
    marginRight: 20,
    pageBreakInside: 'avoid',
  },
  warningSection: {
    marginBottom: 10,
    padding: 8,
    backgroundColor: '#fff3cd',
    borderWidth: 1,
    borderColor: '#ffeaa7',
    borderRadius: 4,
  },
  warningText: {
    fontSize: 8,
    color: '#856404',
    textAlign: 'center',
  },
  pageBreak: {
    marginTop: 10,
    marginBottom: 10,
    textAlign: 'center',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    fontSize: 10,
    color: '#666',
    fontWeight: 'bold',
    textAlign: 'right',
  },
  signatureImage: {
    width: '100%',
    height: 60,
    marginBottom: 5,
  },
  signatureText: {
    fontSize: 8,
    textAlign: 'center',
  },
  signatureDate: {
    fontSize: 8,
    textAlign: 'center',
    marginTop: 5,
    color: '#666',
  },
  productInfo: {
    flexDirection: 'column',
    justifyContent: 'center',
  },
  productImage: {
    width: 25,
    height: 25,
    objectFit: 'contain',
  },
  productText: {
    fontSize: 8,
  },
  colorCircle: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 3,
    borderWidth: 1,
    borderColor: '#000',
  },
  colorInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  colorName: {
    fontSize: 8,
  },
  notes: {
    marginTop: 30,
    padding: 15,
    backgroundColor: '#f8f8f8',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#dddddd',
    pageBreakInside: 'avoid',
  },
  notesTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
    fontSize: 12,
    color: '#333',
  },
  notesText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: '#555',
  },
  notesWarning: {
    fontSize: 8,
    color: '#999',
    fontStyle: 'italic',
    marginTop: 5,
  },
  mainContent: {
    flex: 1,
    paddingBottom: 50,
  },
  totalRow: {
    backgroundColor: '#f8f8f8',
  },
  centerText: {
    textAlign: 'center',
  },
  rightText: {
    textAlign: 'right',
  },
  orderInfoLeft: {
    width: '50%',
  },
  orderInfoRight: {
    width: '50%',
    textAlign: 'right',
  },
  shippingNote: {
    fontSize: 8,
    color: '#666',
    fontStyle: 'italic',
  },
  orderDetailsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  totalItemsBadge: {
    backgroundColor: '#f0f0f0',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginBottom: 8,
    alignSelf: 'flex-start',
  },
  totalItemsText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#333',
  },
});

export const OrderPDF = ({ order, companyInfo, simplified = false }) => {
  // Validación mejorada - más permisiva pero segura
  if (!order) {
    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          <View style={styles.mainContent}>
            <Text style={styles.title}>Error: Pedido no encontrado</Text>
          </View>
        </Page>
      </Document>
    );
  }

  // Validar que items sea un array válido
  const items = Array.isArray(order.items) ? order.items : [];
  
  if (items.length === 0) {
    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          <View style={styles.mainContent}>
            <Text style={styles.title}>Pedido #{order.number || 'N/A'}</Text>
            <Text style={styles.title}>Sin productos en el pedido</Text>
          </View>
        </Page>
      </Document>
    );
  }

  // Normalizar talla a las claves estándar del informe
  const normalizeSize = (size) => {
    if (!size) return 'M';
    const s = String(size).toUpperCase().trim();
    const map = {
      'XS': 'P',
      'S': 'P',
      'P': 'P',
      'M': 'M',
      'L': 'L',
      'XL': 'XL',
      'XXL': '2XL',
      '2XL': '2XL',
      'XXXL': '3XL',
      '3XL': '3XL',
      'XXXXL': '4XL',
      '4XL': '4XL',
      'XXXXXL': '5XL',
      '5XL': '5XL',
      'U': 'M',
      'UNICA': 'M',
      'ÚNICA': 'M',
      'TU': 'M'
    };
    return map[s] || (['P','M','L','XL','2XL','3XL','4XL','5XL'].includes(s) ? s : 'M');
  };

  // Agrupar items por producto con validaciones de seguridad
  const groupedItems = items.reduce((acc, item) => {
    // Validar que el item tenga los datos mínimos necesarios
    if (!item || !item.product || !item.product.code) {
      console.warn('Item inválido encontrado:', item);
      return acc;
    }

    const productCode = item.product.code;
    
    if (!acc[productCode]) {
      acc[productCode] = {
        product: item.product,
        colors: {}
      };
    }
    
    if (!acc[productCode].colors[item.color]) {
      acc[productCode].colors[item.color] = {
        color: item.color,
        price: Number(item.price) || 0,
        sizes: {
          'P': 0, 'M': 0, 'L': 0, 'XL': 0, '2XL': 0, '3XL': 0, '4XL': 0, '5XL': 0
        },
        total: 0
      };
    }
    
    const quantity = Number(item.quantity) || 0;
    const sizeKey = normalizeSize(item.size);

    // Sumar cantidades por talla y total
    acc[productCode].colors[item.color].sizes[sizeKey] += quantity;
    acc[productCode].colors[item.color].total += quantity;
    
    return acc;
  }, {});

  // Renderizar todas las líneas del pedido (sin recorte por producto/color)
  const groupedItemsForRender = groupedItems;

  const getColorRGB = (colorId) => {
    const colorMappings = companyInfo?.color_mappings || {};
    const colorInfo = colorMappings[colorId];
    return colorInfo?.hex || '#000000';
  };

  const getColorName = (colorId) => {
    const colorMappings = companyInfo?.color_mappings || {};
    const colorInfo = colorMappings[colorId];
    return colorInfo?.description || colorId;
  };

  // Truncar descripciones para ahorrar espacio horizontal si es necesario
  const truncateText = (text, maxLength = 60) => {
    if (!text) return 'N/A';
    const s = String(text);
    return s.length > maxLength ? s.slice(0, maxLength) + '…' : s;
  };

  const formatAddress = (parts) => {
    const nonEmptyParts = parts.filter(Boolean);
    return nonEmptyParts.length > 0 ? nonEmptyParts.join(', ') : 'No especificada';
  };

  const calculateTotal = () => {
    let total = 0;
    try {
      if (groupedItems && Object.keys(groupedItems).length > 0) {
        Object.values(groupedItems).forEach(productGroup => {
          if (productGroup && productGroup.colors) {
            Object.values(productGroup.colors).forEach(colorInfo => {
              if (colorInfo && typeof colorInfo.total === 'number' && typeof colorInfo.price === 'number') {
                total += colorInfo.total * colorInfo.price;
              }
            });
          }
        });
      }
    } catch (error) {
      console.error('Error calculando total:', error);
    }
    return Math.round(total * 100) / 100;
  };

  // Función para calcular el total de prendas del pedido
  const calculateTotalItems = () => {
    let totalItems = 0;
    try {
      if (groupedItems && Object.keys(groupedItems).length > 0) {
        Object.values(groupedItems).forEach(productGroup => {
          if (productGroup && productGroup.colors) {
            Object.values(productGroup.colors).forEach(colorInfo => {
              if (colorInfo && typeof colorInfo.total === 'number') {
                totalItems += colorInfo.total;
              }
            });
          }
        });
      }
    } catch (error) {
      console.error('Error calculando total de items:', error);
    }
    return totalItems;
  };

  // Función para formatear las formas de pago
  const formatPaymentMethods = (formaPago) => {
    if (!formaPago || formaPago.length === 0) {
      return 'Contado';
    }
    
    const days = formaPago.map(dias => parseInt(dias));
    if (days.length === 1) {
      return `${days[0]} días`;
    } else if (days.length === 2) {
      return `${days[0]}-${days[1]} días`;
    } else if (days.length === 3) {
      return `${days[0]}-${days[1]}-${days[2]} días`;
    }
    
    return formaPago.join(', ') + ' días';
  };

  // Función para obtener la dirección de envío
  const getShippingAddress = () => {
    if (order.shipping_address) {
      return {
        address: `${order.shipping_address.street}, ${order.shipping_address.city}`,
        isShipping: true
      };
    } else if (order.customer?.street) {
      const fiscalAddress = formatAddress([
        order.customer.street,
        order.customer.city,
        order.customer.province,
        order.customer.postalCode
      ]);
      return {
        address: fiscalAddress,
        isShipping: false
      };
    }
    return {
      address: 'No especificada',
      isShipping: false
    };
  };

  const shippingInfo = getShippingAddress();

    // Crear páginas múltiples manualmente para mejor control
  const createPages = () => {
    const pages = [];
    const rowsPerFirstPage = 8; // Menos filas en primera página por cabecera
    const rowsPerOtherPages = 12; // Más filas en páginas siguientes
    const allRows = [];
    
    // Generar todas las filas primero
    try {
      let rowIndex = 0;
      Object.entries(groupedItemsForRender).forEach(([productCode, productGroup], productIndex) => {
        if (!productGroup || !productGroup.colors) return;
        
        Object.entries(productGroup.colors).forEach(([colorId, colorInfo], colorIndex) => {
          if (!colorInfo || typeof colorInfo !== 'object') {
            console.warn(`Color info inválido: ${productCode}-${colorId}`);
            return;
          }
          
          // Log detallado de los datos del color para debug (solo si hay problemas)
          if (colorInfo.total === 0 && colorInfo.sizes && Object.values(colorInfo.sizes).every(size => !size || size === 0)) {
            console.log(`Color con cantidad 0: ${productCode}-${colorId}`, {
              total: colorInfo.total,
              sizes: colorInfo.sizes
            });
          }
          
          const key = `${productCode}-${colorId}-${productIndex}-${colorIndex}-${rowIndex++}`;
          
          // Validar que los datos sean válidos antes de crear la fila
          // Ser más permisivo: mostrar filas incluso con total 0 o sizes vacíos
          const hasValidTotal = typeof colorInfo.total === 'number';
          const hasValidSizes = colorInfo.sizes && typeof colorInfo.sizes === 'object';
          
          // Solo saltar si no tiene ni total ni sizes definidos
          if (!hasValidTotal && !hasValidSizes) {
            console.warn(`Fila inválida detectada: ${productCode}-${colorId} - sin estructura de datos`);
            return; // Saltar esta fila
          }
          
          // Si tiene total 0 y sizes vacíos, mostrar pero con advertencia
          if (hasValidTotal && colorInfo.total === 0 && hasValidSizes && 
              Object.values(colorInfo.sizes).every(size => !size || size === 0)) {
            console.warn(`Fila con cantidad 0: ${productCode}-${colorId} - se mostrará con 0`);
          }
          
          const showImage = !simplified && productGroup.product?.images?.length > 0 && colorIndex === 0;

          allRows.push(
            <View key={key} style={styles.tableRow}>
              <View style={[styles.tableCol, styles.productCol]}>
                <View style={styles.productInfo}>
                  <Text>{productGroup.product?.code || 'N/A'}</Text>
                  <Text style={styles.productText}>{truncateText(productGroup.product?.description, 60)}</Text>
                </View>
              </View>
              <View style={[styles.tableCol, styles.imageCol]}>
                {showImage ? (
                  <Image
                    style={styles.productImage}
                    src={productGroup.product.images[productGroup.product.main_image_index || 0]}
                    cache={true}
                  />
                ) : (
                  <View style={styles.productImage} />
                )}
              </View>
              <View style={[styles.tableCol, styles.colorCol]}>
                <View style={styles.colorInfo}>
                  <View style={[styles.colorCircle, { backgroundColor: getColorRGB(colorId) }]} />
                  <Text style={styles.colorName}>{getColorName(colorId)}</Text>
                </View>
              </View>
              <Text style={[styles.tableCol, styles.sizeCol, styles.centerText]}>{colorInfo.sizes?.['P'] || '0'}</Text>
              <Text style={[styles.tableCol, styles.sizeCol, styles.centerText]}>{colorInfo.sizes?.['M'] || '0'}</Text>
              <Text style={[styles.tableCol, styles.sizeCol, styles.centerText]}>{colorInfo.sizes?.['L'] || '0'}</Text>
              <Text style={[styles.tableCol, styles.sizeCol, styles.centerText]}>{colorInfo.sizes?.['XL'] || '0'}</Text>
              <Text style={[styles.tableCol, styles.sizeCol, styles.centerText]}>{colorInfo.sizes?.['2XL'] || '0'}</Text>
              <Text style={[styles.tableCol, styles.sizeCol, styles.centerText]}>{colorInfo.sizes?.['3XL'] || '0'}</Text>
              <Text style={[styles.tableCol, styles.sizeCol, styles.centerText]}>{colorInfo.sizes?.['4XL'] || '0'}</Text>
              <Text style={[styles.tableCol, styles.sizeCol, styles.centerText]}>{colorInfo.sizes?.['5XL'] || '0'}</Text>
              <Text style={[styles.tableCol, styles.totalCol, styles.centerText]}>{colorInfo.total || 0}</Text>
              <Text style={[styles.tableCol, styles.priceCol, styles.rightText]}>
                {typeof colorInfo.price === 'number' ? colorInfo.price.toFixed(2) : '0.00'}€
              </Text>
              <Text style={[styles.tableCol, styles.subtotalCol, styles.rightText]}>
                {typeof colorInfo.total === 'number' && typeof colorInfo.price === 'number' 
                  ? (colorInfo.total * colorInfo.price).toFixed(2) 
                  : '0.00'}€
              </Text>
            </View>
          );
        });
      });
    } catch (error) {
      console.error('Error generating rows:', error);
      allRows.push(
        <View key="error-row" style={styles.tableRow}>
          <Text style={[styles.tableCol, { width: '100%' }]}>
            Error renderizando productos: {error.message}
          </Text>
        </View>
      );
    }
    
    // Limpiar filas duplicadas o problemáticas
    const cleanRows = allRows.filter((row, index) => {
      if (!row || !row.props) {
        console.warn(`Fila ${index} eliminada: estructura inválida`);
        return false;
      }
      if (!row.props.children || !Array.isArray(row.props.children)) {
        console.warn(`Fila ${index} eliminada: sin children válidos`);
        return false;
      }
      // Verificar que tenga al menos 12 children (columnas de la tabla)
      if (row.props.children.length < 12) {
        console.warn(`Fila ${index} eliminada: insuficientes columnas (${row.props.children.length})`);
        return false;
      }
      return true;
    });
    
    console.log(`Filas después de limpieza: ${cleanRows.length} de ${allRows.length}`);
    
    // Log detallado solo si hay problemas
    if (cleanRows.length !== allRows.length) {
      console.log('Detalle de filas limpias:', cleanRows.map((row, index) => ({
        index,
        hasProps: !!row.props,
        hasChildren: !!row.props?.children,
        childrenLength: row.props?.children?.length || 0
      })));
    }
    
    // Log de productos y colores procesados
    console.log('Productos procesados:', Object.keys(groupedItemsForRender).length);
    let totalColors = 0;
    Object.entries(groupedItemsForRender).forEach(([productCode, productGroup]) => {
      if (productGroup?.colors) {
        const colorCount = Object.keys(productGroup.colors).length;
        totalColors += colorCount;
        console.log(`Producto ${productCode}: ${colorCount} colores`);
      }
    });
    console.log(`Total de colores procesados: ${totalColors}`);
    console.log(`Total de filas generadas: ${allRows.length}`);
    console.log(`Total de filas después de limpieza: ${cleanRows.length}`);
    
    // Si no hay filas, crear una página vacía
    if (cleanRows.length === 0) {
      return [
        <Page key="empty-page" size="A4" orientation="landscape" style={styles.page}>
          <View style={styles.mainContent}>
            <Text style={styles.title}>Sin productos en el pedido</Text>
          </View>
        </Page>
      ];
    }
    
    // Dividir filas en páginas con capacidad diferente para la primera página
    console.log(`Total de filas generadas: ${cleanRows.length}`);
    console.log(`Filas por 1ª página: ${rowsPerFirstPage} | resto: ${rowsPerOtherPages}`);
    
    let index = 0;
    let pageNumber = 1;
    while (index < cleanRows.length) {
      const capacity = pageNumber === 1 ? rowsPerFirstPage : rowsPerOtherPages;
      const endIndex = Math.min(index + capacity, cleanRows.length);
      const pageRows = cleanRows.slice(index, endIndex);
      index = endIndex;
      const isLastPage = index >= cleanRows.length;
      
      console.log(`Página ${pageNumber}: ${pageRows.length} filas`);
      
      // Verificar que todas las filas sean válidas
      const validRows = pageRows.filter(row => {
        const isValid = row && row.props && row.props.children && Array.isArray(row.props.children);
        if (!isValid) {
          console.warn(`Fila inválida en página ${pageNumber}:`, row);
        }
        return isValid;
      });
      
      if (validRows.length !== pageRows.length) {
        console.warn(`Página ${pageNumber}: ${pageRows.length - validRows.length} filas inválidas detectadas`);
      }
      
      console.log(`Página ${pageNumber}: ${validRows.length} filas válidas de ${pageRows.length} totales`);
      
      // Solo crear página si hay filas válidas
      if (validRows.length > 0) {
        pages.push(
          <Page key={`page-${pageNumber}`} size="A4" orientation="landscape" style={styles.page}>
          <View style={styles.mainContent}>
            {pageNumber === 1 && (
              <View style={styles.header}>
                <View style={styles.companyInfo}>
                  <Text style={styles.companyName}>{companyInfo?.nombre_empresa || 'Empresa'}</Text>
                  <Text style={styles.companyDetails}>{companyInfo?.calle_empresa || ''}</Text>
                  <Text style={styles.companyDetails}>
                    {formatAddress([
                      companyInfo?.poblacion_empresa,
                      companyInfo?.provincia_empresa
                    ])}
                  </Text>
                  <Text style={styles.companyDetails}>CIF: {companyInfo?.cif_empresa || ''}</Text>
                </View>
                <View style={styles.customerInfo}>
                  <Text style={styles.title}>Pedido #{order.number}</Text>
                  <Text style={styles.companyName}>{order.customer?.name}</Text>
                  {!!order.customer?.titular && (
                    <Text style={styles.companyDetails}>{`Nombre Comercial: ${order.customer.titular}`}</Text>
                  )}
                  <View style={styles.totalItemsBadge}>
                    <Text style={styles.totalItemsText}>Total Prendas: {calculateTotalItems()} unidades</Text>
                  </View>
                  <View style={styles.orderDetailsContainer}>
                    <View style={styles.orderInfoLeft}>
                      <Text style={styles.orderDetails}>Fecha: {new Date(order.date).toLocaleDateString()}</Text>
                      <Text style={styles.orderDetails}>Fecha de Entrega: {order.fecha_entrega ? new Date(order.fecha_entrega).toLocaleDateString() : 'No especificada'}</Text>
                      <Text style={styles.orderDetails}>Formas de Pago: {formatPaymentMethods(order.forma_pago)}</Text>
                      {!!(order.forma_pago && order.forma_pago.includes('90') && !order.confirmacion_90dias) && (
                        <Text style={styles.orderDetails}>{'⚠️ Pago a 90 días pendiente de confirmación'}</Text>
                      )}
                      {!!order.payment_transfer && (
                        <Text style={styles.orderDetails}>{'💳 Pago mediante transferencia bancaria'}</Text>
                      )}
                      {!!(order.discount_percentage > 0) && (
                        <Text style={styles.orderDetails}>{`💰 Descuento del ${order.discount_percentage}% aplicado a los precios`}</Text>
                      )}
                    </View>
                    <View style={styles.orderInfoRight}>
                      <Text style={styles.orderDetails}>
                        {`Dirección: ${shippingInfo.address}${shippingInfo.isShipping ? ' (Dirección de envío)' : ''}`}
                      </Text>
                      <Text style={styles.orderDetails}>Teléfono: {order.customer?.phone || 'No especificado'}</Text>
                      <Text style={styles.orderDetails}>Email: {order.customer?.email || 'No especificado'}</Text>
                    </View>
                  </View>
                </View>
              </View>
            )}

            <View style={styles.section}>
              <View style={styles.table}>
                {/* Header de la tabla en cada página */}
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <Text style={[styles.tableCol, styles.productCol]}>Producto</Text>
                  <Text style={[styles.tableCol, styles.imageCol]}>Imagen</Text>
                  <Text style={[styles.tableCol, styles.colorCol]}>Color</Text>
                  <Text style={[styles.tableCol, styles.sizeCol]}>P</Text>
                  <Text style={[styles.tableCol, styles.sizeCol]}>M</Text>
                  <Text style={[styles.tableCol, styles.sizeCol]}>L</Text>
                  <Text style={[styles.tableCol, styles.sizeCol]}>XL</Text>
                  <Text style={[styles.tableCol, styles.sizeCol]}>2XL</Text>
                  <Text style={[styles.tableCol, styles.sizeCol]}>3XL</Text>
                  <Text style={[styles.tableCol, styles.sizeCol]}>4XL</Text>
                  <Text style={[styles.tableCol, styles.sizeCol]}>5XL</Text>
                  <Text style={[styles.tableCol, styles.totalCol]}>Total</Text>
                  <Text style={[styles.tableCol, styles.priceCol]}>Precio</Text>
                  <Text style={[styles.tableCol, styles.subtotalCol]}>Subtotal</Text>
                </View>

                {validRows}

                {/* Total solo en la última página */}
                {isLastPage && (
                  <View>
                    {!!(order.discount_percentage > 0) && (
                      <View style={[styles.tableRow, { backgroundColor: '#f0f9ff', borderTopWidth: 1, borderTopColor: '#e0e7ff' }]}> 
                        <Text style={[styles.tableCol, { width: '100%', textAlign: 'center', fontSize: 10, color: '#1e40af', fontStyle: 'italic' }]}> 
                          💰 Se ha aplicado un descuento del {order.discount_percentage}% sobre todos los precios mostrados
                        </Text>
                      </View>
                    )}
                    <View style={[styles.tableRow, styles.totalRow]}>
                      <Text style={[styles.tableCol, { width: '84%' }]}>Total Pedido</Text>
                      <Text style={[styles.tableCol, { width: '16%', textAlign: 'right' }]}> 
                        {calculateTotal().toFixed(2)}€
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            </View>

            {/* Número de página (footer absoluto) */}
            <Text style={styles.pageNumber}>Página {pageNumber}</Text>

            {!!order.notes && isLastPage && (
              <View style={{
                ...styles.notes,
                borderTopWidth: 1,
                borderTopColor: '#e5e7eb'
              }}>
                <Text style={styles.notesTitle}>Observaciones:</Text>
                <Text style={styles.notesText}>
                  {order.notes.length > 500 ? order.notes.substring(0, 500) + '...' : order.notes}
                </Text>
                {!!(order.notes.length > 500) && (
                  <Text style={styles.notesWarning}>
                    (Observaciones truncadas por longitud. Total: {order.notes.length} caracteres)
                  </Text>
                )}
              </View>
            )}

            {!!order.signature && isLastPage && (
              <View style={styles.signature}>
                <Image
                  style={styles.signatureImage}
                  src={order.signature}
                />
                <Text style={styles.signatureText}>Firma del Cliente</Text>
                <Text style={styles.signatureDate}>
                  Fecha: {new Date(order.date).toLocaleDateString()}
                </Text>
              </View>
            )}
          </View>
        </Page>
        );
      } else {
        console.warn(`Página ${pageNumber} omitida por no tener filas válidas`);
      }
      pageNumber += 1;
    }
    
    console.log(`Total de páginas creadas: ${pages.length}`);
    console.log('Resumen de páginas:', pages.map((page, index) => ({
      pageNumber: index + 1,
      hasContent: !!page.props?.children?.length
    })));
    
    return pages;
  };

  return (
    <Document>
      {createPages()}
    </Document>
  );
}; 
